import { z } from 'zod';

import { type V3DataPresentation } from '@apps/web/features/phoebe_chat_v3/model/transcript';

const PRESENTATION_PATH_PATTERN =
    /^\/workspace\/presentations\/[a-f0-9]{24}\.jsonl$/;
const QUERY_OUTPUT_PATH_PATTERN =
    /^\/workspace\/tool_outputs\/[0-9]{4,}_query\.jsonl$/;
const TABLE_PATH_PATTERN = /^\/workspace\/tables\/[a-f0-9]{24}\.jsonl$/;
const selectionActionSchema = z
    .object({
        label: z
            .string()
            .trim()
            .min(1)
            .max(48)
            .refine((label) => label.split(/\s+/).length <= 5),
        prompt: z.string().trim().min(1).max(500),
    })
    .strict();
const DOMAIN_PRESENTATION_ENTITIES = {
    shifts: 'shift',
    caregivers: 'caregiver',
    client_detail: 'client',
} as const;

// V3 owns this receipt shape so new v3 cards cannot become valid legacy
// dynamic widgets merely because both renderers consume the same tool event.
const v3PresentationReceiptSchema = z.object({
    presentation: z.enum([
        'shifts',
        'caregivers',
        'caregiver_detail',
        'client_detail',
    ]),
    artifact_path: z.string().min(1),
    format: z.literal('jsonl'),
    row_count: z.number().int().nonnegative(),
    byte_size: z.number().int().nonnegative(),
    columns: z.array(z.string().max(128)).max(64),
    columns_truncated: z.boolean(),
    content_sha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .optional(),
    event_id: z.uuid().optional(),
});

const v3PresentationOutputSchema = z.object({
    presentation_artifact: v3PresentationReceiptSchema,
});

const renderArtifactReceiptSchema = z.object({
    component: z.enum(['table', 'shifts', 'caregivers', 'client_detail']),
    path: z.string().min(1),
    format: z.enum(['csv', 'jsonl']),
    row_count: z.number().int().nonnegative(),
    byte_size: z.number().int().nonnegative(),
    columns: z.array(z.string().max(128)).max(64),
    columns_truncated: z.boolean(),
    content_sha256: z.string().regex(/^[a-f0-9]{64}$/),
    context_entity: z.enum(['shift', 'caregiver', 'client']).optional(),
    selection_actions: z.array(selectionActionSchema).min(1).max(4).optional(),
    event_id: z.uuid().optional(),
});

const renderArtifactOutputSchema = z.object({
    render_artifact: renderArtifactReceiptSchema,
});

function entityForPresentation(
    presentation: 'shifts' | 'caregivers' | 'client_detail',
): 'shift' | 'caregiver' | 'client' {
    return DOMAIN_PRESENTATION_ENTITIES[presentation];
}

export function getV3DataPresentationEntity(
    presentation: V3DataPresentation,
): 'shift' | 'caregiver' | 'client' | null {
    return presentation.presentation === 'table'
        ? (presentation.entity ?? null)
        : entityForPresentation(presentation.presentation);
}

function resolveEventId(
    eventId: string | null | undefined,
    receiptEventId?: string,
) {
    const parsed = z.uuid().safeParse(eventId);
    return parsed.success ? parsed.data : receiptEventId;
}

export function parseV3DataPresentation({
    toolName,
    runId,
    eventId,
    output,
}: {
    readonly toolName: string;
    readonly runId: string | null | undefined;
    readonly eventId?: string | null;
    readonly output: unknown;
}): V3DataPresentation | null {
    if (!runId) {
        return null;
    }
    if (toolName === 'render_dynamic_ui') {
        const parsed = renderArtifactOutputSchema.safeParse(output);
        if (!parsed.success) {
            return null;
        }
        const receipt = parsed.data.render_artifact;
        const resolvedEventId = resolveEventId(eventId, receipt.event_id);
        const artifactPath = receipt.path.trim();
        const pathIsAllowed =
            receipt.component === 'table'
                ? TABLE_PATH_PATTERN.test(artifactPath)
                : PRESENTATION_PATH_PATTERN.test(artifactPath);
        if (
            !pathIsAllowed ||
            receipt.row_count === 0 ||
            (receipt.component === 'client_detail' &&
                receipt.row_count !== 1) ||
            (receipt.component !== 'table' && receipt.format !== 'jsonl')
        ) {
            return null;
        }
        return {
            kind: 'data-presentation',
            version: 1,
            source: 'render',
            presentation: receipt.component,
            entity:
                receipt.component === 'table'
                    ? (receipt.context_entity ?? null)
                    : entityForPresentation(receipt.component),
            format: receipt.format,
            runId,
            filePath: artifactPath.slice('/workspace/'.length),
            rowCount: receipt.row_count,
            columns: receipt.columns,
            ...(receipt.selection_actions === undefined
                ? {}
                : { selectionActions: receipt.selection_actions }),
            contentSha256: receipt.content_sha256,
            ...(resolvedEventId === undefined
                ? {}
                : { eventId: resolvedEventId }),
        };
    }

    const parsed = v3PresentationOutputSchema.safeParse(output);
    if (!parsed.success || (toolName !== 'run_bash' && toolName !== 'query')) {
        return null;
    }
    const receipt = parsed.data.presentation_artifact;
    const resolvedEventId = resolveEventId(eventId, receipt.event_id);
    const presentation =
        receipt.presentation === 'caregiver_detail'
            ? 'caregivers'
            : receipt.presentation;
    const artifactPath = receipt.artifact_path.trim();
    const source = toolName === 'run_bash' ? 'bash' : 'query';
    const pathIsAllowed =
        PRESENTATION_PATH_PATTERN.test(artifactPath) ||
        (source === 'query' && QUERY_OUTPUT_PATH_PATTERN.test(artifactPath));
    if (
        !pathIsAllowed ||
        receipt.row_count === 0 ||
        ((receipt.presentation === 'caregiver_detail' ||
            receipt.presentation === 'client_detail') &&
            receipt.row_count !== 1)
    ) {
        return null;
    }
    return {
        kind: 'data-presentation',
        version: 1,
        source,
        presentation,
        runId,
        filePath: artifactPath.slice('/workspace/'.length),
        rowCount: receipt.row_count,
        columns: receipt.columns,
        ...(receipt.content_sha256 === undefined
            ? {}
            : { contentSha256: receipt.content_sha256 }),
        ...(resolvedEventId === undefined ? {} : { eventId: resolvedEventId }),
    };
}
